import { useState } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { useNotify } from "@/hooks/useNotify";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Eye, EyeOff } from "lucide-react";

interface ForcedReauthModalProps {
  isOpen: boolean;
  onClose: () => void;
  userEmail?: string;
}

export const ForcedReauthModal = ({ isOpen, onClose, userEmail }: ForcedReauthModalProps) => {
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const { signInWithEmailAndPassword } = useAuth();
  const notify = useNotify();

  const handleReauth = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!userEmail || !password) {
      notify.error("Please enter your password.");
      return;
    }

    try {
      setIsLoading(true);
      await signInWithEmailAndPassword(userEmail, password);
      
      // Clear the forced reauth flag
      localStorage.removeItem('forced_reauth_required');
      
      notify.success("Re-authentication successful. You can continue using the application.");
      
      onClose();
    } catch (error: any) {
      console.error("Re-authentication error:", error);
      notify.error(error.message || "Please check your password and try again.");
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={() => {}}>
      <DialogContent className="sm:max-w-md" onPointerDownOutside={(e) => e.preventDefault()}>
        <DialogHeader>
          <DialogTitle>Security Check Required</DialogTitle>
          <DialogDescription>
            For your security, please re-enter your password. Your session has been active for 14 days.
          </DialogDescription>
        </DialogHeader>
        
        <form onSubmit={handleReauth} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="email">Email</Label>
            <Input
              id="email"
              type="email"
              value={userEmail || ""}
              disabled
              className="bg-muted"
            />
          </div>
          
          <div className="space-y-2">
            <Label htmlFor="password">Password</Label>
            <div className="relative">
              <Input
                id="password"
                type={showPassword ? "text" : "password"}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Enter your password"
                required
                className="pr-10"
              />
              <Button
                type="button"
                variant="ghost"
                size="sm"
                className="absolute right-0 top-0 h-full px-3 py-2 hover:bg-transparent"
                onClick={() => setShowPassword(!showPassword)}
              >
                {showPassword ? (
                  <EyeOff className="h-4 w-4" />
                ) : (
                  <Eye className="h-4 w-4" />
                )}
              </Button>
            </div>
          </div>
          
          <div className="flex justify-end space-x-2">
            <Button
              type="submit"
              disabled={isLoading || !password}
              className="w-full"
            >
              {isLoading ? "Verifying..." : "Continue"}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};